import { Box, Stack, useTheme } from "@mui/material";
import type { QueryKey } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { NotificationCenterKeys } from "web_myplann/Api/NotificationCenter/notificationCenterKeys";
import {
	useGoalsRightOptional,
	useNotificationCenterRightOptional,
} from "web_myplann/Api/Rights/RightsProvider";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { NotificationCenterLegacyTopBar } from "web_myplann/NotificationCenter/Components/NotificationCenterLegacyTopBar";
import { NotificationCenterTabContent } from "web_myplann/NotificationCenter/Components/NotificationCenterTabContent";
import { NotificationCenterTabSwitch } from "web_myplann/NotificationCenter/Components/NotificationCenterTabSwitch";
import { NotificationCenterV2Body } from "web_myplann/NotificationCenter/Components/NotificationCenterV2Body";
import {
	DEFAULT_NOTIFICATION_CENTER_TAB,
	isNotificationCenterTab,
	NOTIFICATION_CENTER_TAB_QUERY_PARAM,
	type NotificationCenterTab,
} from "web_myplann/NotificationCenter/Constants/notificationCenterTabs";
import { NativePullToRefresh } from "web_myplann/Shared/Components/NativePullToRefresh";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import { MyPlannNavigationHeader } from "web_myplann/Shared/Header/MyPlannNavigationHeader";
import { rightsState } from "web_myplann/Shared/State/rightsAtom";

const NOTIFICATION_CENTER_QUERY_KEYS: QueryKey[] = [NotificationCenterKeys.all];

export const NotificationCenterPage = () => {
	const { t } = useResource();
	const theme = useTheme();
	const [searchParams, setSearchParams] = useSearchParams();
	// Defenziva pro přímé routování; primární gate je NotificationsRouteSwitch.
	const isRightsPending = useAtomValue(rightsState) === undefined;
	const hasNotificationCenterRight = useNotificationCenterRightOptional();
	const hasGoalsRight = useGoalsRightOptional();

	const urlTab = searchParams.get(NOTIFICATION_CENTER_TAB_QUERY_PARAM);
	const activeTab: NotificationCenterTab = isNotificationCenterTab(urlTab)
		? urlTab
		: DEFAULT_NOTIFICATION_CENTER_TAB;

	useEffect(() => {
		if (isRightsPending || !hasNotificationCenterRight) {
			return;
		}
		if (!isNotificationCenterTab(urlTab)) {
			const next = new URLSearchParams(searchParams);
			next.set(
				NOTIFICATION_CENTER_TAB_QUERY_PARAM,
				DEFAULT_NOTIFICATION_CENTER_TAB,
			);
			setSearchParams(next, { replace: true });
		}
	}, [
		urlTab,
		searchParams,
		setSearchParams,
		isRightsPending,
		hasNotificationCenterRight,
	]);

	const handleTabChange = (nextTab: NotificationCenterTab) => {
		const next = new URLSearchParams(searchParams);
		next.set(NOTIFICATION_CENTER_TAB_QUERY_PARAM, nextTab);
		// Tab switches must not create history entries — the back arrow should
		// leave the notification center, not walk through previously open tabs.
		setSearchParams(next, { replace: true });
	};

	if (isRightsPending) {
		return null;
	}

	if (!hasNotificationCenterRight) {
		return <Navigate to={Routing.Dashboard.path()} replace />;
	}

	return (
		<MyPlannLayout
			disableContentPadding
			bgColor={hasGoalsRight ? theme.palette.surface.muted : undefined}
			topBarBgColor={hasGoalsRight ? theme.palette.background.paper : undefined}
			topBar={
				hasGoalsRight ? (
					<Stack width="100%" bgcolor={theme.palette.background.paper}>
						<MyPlannNavigationHeader
							title={t(Resources.NotificationCenter.Title)}
							defaultRoute={Routing.Dashboard.path()}
							bgColor={theme.palette.background.paper}
						/>
						<NotificationCenterTabSwitch
							activeTab={activeTab}
							onTabChange={handleTabChange}
						/>
					</Stack>
				) : (
					<NotificationCenterLegacyTopBar
						activeTab={activeTab}
						onTabChange={handleTabChange}
					/>
				)
			}
		>
			<NativePullToRefresh
				spinnerOffset={-25}
				queryKeys={NOTIFICATION_CENTER_QUERY_KEYS}
				fillHeight={hasGoalsRight}
			>
				{hasGoalsRight && <NotificationCenterV2Body activeTab={activeTab} />}
				{!hasGoalsRight && (
					<Box pt={1.6} data-testid="notification-center-page">
						<NotificationCenterTabContent activeTab={activeTab} />
					</Box>
				)}
			</NativePullToRefresh>
		</MyPlannLayout>
	);
};
